// Service WO write-back client — the "Preparar facturación" workspace.
//
// One read (the picker feed) + three writes, mirroring the existing write
// modules (anticipos/service): CSRF token from the `pacebi_csrf` cookie echoed
// as `X-CSRF-Token`, `credentials: 'same-origin'` on every call, and an
// `idempotency_key` minted into the labor-post body (the only verb whose Go
// handler reads one). Errors surface the daemon's message text.
//
// Server: GET /api/service/catalog (view:service), POST /api/service/{tech,job}
// (service:catalog), POST /api/service/wo-jobs (service:wo-post). All gated by
// the service_shop feature + writes_enabled. See handlers_service_writeback.go
// and handle-service-catalog in paceAPIserv.p.

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

// --- read: the picker feed ---

export interface CatalogJob {
  job_code: string;
  job_desc: string;
  category: string;
  cust_charge: boolean;
  cust_rate: number;
  tech_comm: number;
  taxable: boolean;
  supplies: boolean;
  job_level: number;
}

export interface CatalogCategory {
  category: string;
  cat_desc: string;
}

export interface CatalogTech {
  ss_tech: string;
  name: string;
}

export interface ServiceCatalog {
  ok: boolean;
  jobs: CatalogJob[];
  categories: CatalogCategory[];
  techs: CatalogTech[];
}

export async function fetchServiceCatalog(sid: number): Promise<ServiceCatalog> {
  const res = await fetch(`/api/service/catalog?sid=${sid}`, { credentials: 'same-origin' });
  if (!res.ok) {
    if (res.status === 404) throw new Error('404: módulo de taller no habilitado');
    throw new Error(`service-catalog failed: ${res.status}`);
  }
  return (await res.json()) as ServiceCatalog;
}

// --- writes: the daemon returns { ok, data } | { ok:false, error } ---

export interface WriteResp<T> {
  ok: boolean;
  data?: T;
  error?: string;
}

export interface WoJobData {
  tnum: number;
  slot: number;
  job_num: number;
  job_comm: number;
  wo_job_total: number;
}

/** Post ONE labor line to an open WO (the ABL verb is single-line). */
export function postWoJob(params: {
  sid: number;
  tnum: number;
  job_code: string;
  tech: string;
  status?: string;
  est_hrs: number;
  act_hrs: number;
  price: number;
}): Promise<WriteResp<WoJobData>> {
  return postJSON<WriteResp<WoJobData>>('/api/service/wo-jobs', {
    ...params,
    status: params.status ?? ' ',
    idempotency_key: crypto.randomUUID(),
  });
}

export interface CreateJobData {
  job_code: string;
  seq: number;
  created: boolean;
}

/** Create a service-job catalog row (manager-only: service:catalog). */
export function createSsJob(params: {
  sid: number;
  job_code: string;
  job_desc: string;
  category: string;
  taxable: boolean;
  supplies: boolean;
  cust_charge: boolean;
  job_time: number;
  job_level: number;
  cust_rate: number;
  tech_comm: number;
  tech_fixed: number;
}): Promise<WriteResp<CreateJobData>> {
  return postJSON<WriteResp<CreateJobData>>('/api/service/job', params);
}

export interface CreateTechData {
  ss_tech: string;
  created: boolean;
}

/** Create a technician (manager-only: service:catalog). rate_hrs/rate_pcnt are
 * EXTENT-6 arrays server-side; the panel sets only the primary slot. */
export function createSsTech(params: {
  sid: number;
  ss_tech: string;
  name: string;
  scom: number;
  rate_hrs: number[];
  rate_pcnt: number[];
}): Promise<WriteResp<CreateTechData>> {
  return postJSON<WriteResp<CreateTechData>>('/api/service/tech', params);
}
